import { injectAesthetic } from '../ui/shared-styles.ts';

export class FormInput extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'type', 'name', 'value', 'placeholder', 'required', 'min', 'disabled', 'error'];
    }

    private innerInput: HTMLInputElement | null = null;

    private seed: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.seed = Math.floor(Math.random() * 10000);
    }

    connectedCallback() {
        this.render();
        this.innerInput = this.shadowRoot?.querySelector('input') || null;
        this.setupEventListeners();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;

        if (name === 'value' && this.innerInput) {
            if (this.innerInput.value !== newValue) {
                this.innerInput.value = newValue;
            }
        } else if (name === 'error' && this.shadowRoot) {
            const errorMsg = this.shadowRoot.querySelector('.error-msg');
            if (errorMsg) {
                errorMsg.textContent = newValue || '';
                (errorMsg as HTMLElement).style.display = newValue ? 'block' : 'none';
            } else if (newValue) {
                this.render(); // Fallback if error-msg doesn't exist yet
            }
        } else if (this.innerInput) {
            // For other attributes that might affect the input structure, we may still need render
            // but we try to be selective. For simplicity, we re-render if it's a structural change.
            this.render();
        }
    }

    private setupEventListeners() {
        if (!this.innerInput) return;

        this.innerInput.addEventListener('input', (e) => {
            const tempValue = (e.target as HTMLInputElement).value;
            // Reflect value back to attribute for consistency if needed, 
            // but primarily dispatch event
            this.dispatchEvent(new CustomEvent('form-input', {
                bubbles: true,
                composed: true,
                detail: { value: tempValue, name: this.getAttribute('name') }
            }));
        });

        this.innerInput.addEventListener('change', (e) => {
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                composed: true,
                detail: { value: (e.target as HTMLInputElement).value, name: this.getAttribute('name') }
            }));
        });
    }

    get value() {
        return this.innerInput?.value || '';
    }

    set value(val: string) {
        if (this.innerInput) {
            this.innerInput.value = val;
        }
        this.setAttribute('value', val);
    }

    get name() {
        return this.getAttribute('name') || '';
    }

    set name(val: string) {
        this.setAttribute('name', val);
    }

    render() {
        const label = this.getAttribute('label') || '';
        const type = this.getAttribute('type') || 'text';
        const name = this.getAttribute('name') || '';
        const value = this.getAttribute('value') || '';
        const placeholder = this.getAttribute('placeholder') || '';
        const required = this.hasAttribute('required');
        const disabled = this.hasAttribute('disabled');
        const min = this.getAttribute('min');
        const error = this.getAttribute('error');

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                ${injectAesthetic(this.seed)}
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: var(--font-main);
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    label {
                        color: var(--accent);
                        font-size: 0.7rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.15em;
                        opacity: 0.8;
                    }
                    input {
                        width: 100%;
                        padding: 0.75rem;
                        background: var(--bg-card);
                        border: 1px solid var(--border);
                        border-radius: var(--radius-md);
                        color: var(--text-main);
                        font-size: 0.9rem;
                        font-family: inherit;
                        box-sizing: border-box;
                        transition: all 0.2s;
                    }
                    input:focus {
                        outline: none;
                        border-color: var(--accent);
                        box-shadow: 2px 2px 0px var(--accent-soft);
                    }
                    input:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    .error-msg {
                        color: #ef4444;
                        font-size: 0.8rem;
                        margin-top: 0.25rem;
                    }
                </style>
                <div class="form-group">
                    ${label ? `<label for="${name}">${label} ${required ? '*' : ''}</label>` : ''}
                    <input 
                        type="${type}" 
                        id="${name}" 
                        name="${name}" 
                        value="${value}" 
                        placeholder="${placeholder}"
                        ${required ? 'required' : ''}
                        ${disabled ? 'disabled' : ''}
                        ${min ? `min="${min}"` : ''}
                    />
                    ${error ? `<div class="error-msg">${error}</div>` : ''}
                </div>
            `;

            // Re-attach event listeners after innerHTML replacement
            this.innerInput = this.shadowRoot.querySelector('input');
            this.setupEventListeners();
        }
    }
}

customElements.define('form-input', FormInput);
